// src/services/workflowService.js
import axios from 'axios';
import { API_CONFIG } from '../config/api';

const API_BASE_URL = API_CONFIG.BASE_URL;

const api = () => {
  const token = localStorage.getItem('token') || localStorage.getItem('authToken');
  if (!token) throw new Error('No authentication token found. Please login again.');
  return axios.create({
    baseURL: API_BASE_URL,
    headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
  });
};

const handle = (error, op) => {
  const detail = error.response?.data?.detail;
  if (error.response?.status === 401) throw new Error('Authentication failed.');
  if (detail) {
    if (Array.isArray(detail)) throw new Error(detail.map(e => e.msg || JSON.stringify(e)).join('; '));
    throw new Error(typeof detail === 'string' ? detail : JSON.stringify(detail));
  }
  throw new Error(error.message || `${op} failed`);
};

export const workflowService = {
  listWorkflows: async () => {
    try {
      const r = await api().get('/api/workflows');
      return r.data;
    } catch (e) { handle(e, 'List workflows'); }
  },

  getWorkflow: async (workflowId) => {
    try {
      const r = await api().get(`/api/workflows/${workflowId}`);
      return r.data;
    } catch (e) { handle(e, 'Get workflow'); }
  },

  createWorkflow: async (data) => {
    try {
      const r = await api().post('/api/workflows', data);
      if (r.data?.success === false) throw new Error(r.data.error || 'Create failed');
      return r.data;
    } catch (e) { handle(e, 'Create workflow'); }
  },

  updateWorkflow: async (workflowId, data) => {
    try {
      const r = await api().put(`/api/workflows/${workflowId}`, data);
      if (r.data?.success === false) throw new Error(r.data.error || 'Update failed');
      return r.data;
    } catch (e) { handle(e, 'Update workflow'); }
  },

  deleteWorkflow: async (workflowId) => {
    try {
      const r = await api().delete(`/api/workflows/${workflowId}`);
      return r.data;
    } catch (e) { handle(e, 'Delete workflow'); }
  },

  toggleEnabled: async (workflowId) => {
    try {
      const r = await api().patch(`/api/workflows/${workflowId}/toggle-enabled`);
      return r.data;
    } catch (e) { handle(e, 'Toggle workflow'); }
  },

  executeWorkflow: async (workflowId, inputText) => {
    try {
      const r = await api().post(`/api/workflows/${workflowId}/execute`, { input_text: inputText });
      return r.data;
    } catch (e) { handle(e, 'Execute workflow'); }
  },

  // SSE streaming execution — calls onEvent(type, data) for each server event
  // event types: 'node_start', 'node_done', 'done', 'error'
  executeWorkflowStream: async (workflowId, inputText, onEvent) => {
    const token = localStorage.getItem('token') || localStorage.getItem('authToken');
    if (!token) throw new Error('No authentication token found.');

    const response = await fetch(
      `${API_BASE_URL}/api/workflows/${workflowId}/execute-stream`,
      {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ input_text: inputText }),
      }
    );

    if (!response.ok) {
      const text = await response.text();
      throw new Error(`Stream request failed: ${response.status} ${text}`);
    }

    const reader = response.body.getReader();
    const decoder = new TextDecoder();
    let buffer = '';
    let currentEvent = null;

    while (true) {
      const { done, value } = await reader.read();
      if (done) break;

      buffer += decoder.decode(value, { stream: true });
      const lines = buffer.split('\n');
      buffer = lines.pop(); // keep last incomplete line

      for (const line of lines) {
        if (line.startsWith('event: ')) {
          currentEvent = line.slice(7).trim();
        } else if (line.startsWith('data: ')) {
          try {
            const data = JSON.parse(line.slice(6));
            if (currentEvent) onEvent(currentEvent, data);
          } catch (_) { /* ignore malformed JSON */ }
          currentEvent = null;
        }
      }
    }
  },

  listExecutions: async (workflowId) => {
    try {
      const r = await api().get(`/api/workflows/${workflowId}/executions`);
      return r.data;
    } catch (e) { handle(e, 'List executions'); }
  },

  // ── Workflow Monitoring (Automate Workflow tab) ──────────────────────────

  listScheduledWorkflows: async () => {
    try {
      const r = await api().get('/api/workflow-schedules');
      return r.data;
    } catch (e) { handle(e, 'List scheduled workflows'); }
  },

  registerWorkflow: async (workflowId, scheduleType, scheduleConfig) => {
    try {
      const body = { workflow_id: workflowId };
      if (scheduleType) {
        body.schedule_type   = scheduleType;
        body.schedule_config = scheduleConfig || {};
      }
      const r = await api().post('/api/workflow-schedules', body);
      return r.data;
    } catch (e) { handle(e, 'Register workflow'); }
  },

  unregisterWorkflow: async (workflowId) => {
    try {
      const r = await api().delete(`/api/workflow-schedules/${workflowId}`);
      return r.data;
    } catch (e) { handle(e, 'Unregister workflow'); }
  },

  runWorkflowNow: async (workflowId) => {
    try {
      const r = await api().post(`/api/workflow-schedules/${workflowId}/run-now`);
      return r.data;
    } catch (e) { handle(e, 'Run workflow now'); }
  },

  updateWorkflowSchedule: async (workflowId, scheduleType, scheduleConfig) => {
    try {
      const r = await api().patch(`/api/workflow-schedules/${workflowId}`, {
        schedule_type: scheduleType, schedule_config: scheduleConfig,
      });
      return r.data;
    } catch (e) { handle(e, 'Update workflow schedule'); }
  },

  toggleWorkflowActive: async (workflowId) => {
    try {
      const r = await api().patch(`/api/workflow-schedules/${workflowId}/toggle`);
      return r.data;
    } catch (e) { handle(e, 'Toggle workflow active'); }
  },

  getFilteredExecutions: async (workflowId, filters = {}) => {
    try {
      const params = new URLSearchParams();
      if (filters.status && filters.status !== 'all') params.set('status', filters.status);
      if (filters.from_date) params.set('from_date', filters.from_date);
      if (filters.to_date)   params.set('to_date',   filters.to_date);
      if (filters.limit)     params.set('limit',     filters.limit);
      const r = await api().get(`/api/workflow-schedules/${workflowId}/executions?${params}`);
      return r.data;
    } catch (e) { handle(e, 'Get filtered executions'); }
  },

  getExecutionDetail: async (executionId) => {
    try {
      const r = await api().get(`/api/workflow-executions/${executionId}`);
      return r.data;
    } catch (e) { handle(e, 'Get execution detail'); }
  },

  listDatasourceFiles: async () => {
    try {
      const r = await api().get('/api/datasource-files');
      return r.data;
    } catch (e) { handle(e, 'List datasource files'); }
  },
};

export default workflowService;
